import React, { useState, useEffect, useCallback } from 'react';
import PreviewRecordSelector from './PreviewRecordSelector';

const TemplatePreviewPane = ({
  htmlContent,
  emailType,
  requiredContexts = [],
  isExpanded = false,
  onToggleExpand,
  onOpenFullscreen
}) => {
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewErrors, setPreviewErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [dataUsed, setDataUsed] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Send test email state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Debounced preview loading
  const loadPreview = useCallback(async () => {
    if (!htmlContent) {
      setPreviewHtml('');
      setPreviewErrors([]);
      return;
    }

    setLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch('/admin/email_templates/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          html_content: htmlContent,
          email_type: emailType,
          company_id: selectedCompanyId,
          event_id: selectedEventId,
          user_id: selectedUserId
        })
      });

      const data = await response.json();
      setPreviewHtml(data.html || '');
      setPreviewErrors(data.errors || []);
      setDataUsed(data.data_used);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewErrors(['Failed to load preview']);
    } finally {
      setLoading(false);
    }
  }, [htmlContent, emailType, selectedCompanyId, selectedEventId, selectedUserId]);

  // Auto-refresh preview when content or selection changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadPreview();
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [htmlContent, selectedCompanyId, selectedEventId, selectedUserId]);

  const handleSelectionChange = ({ companyId, eventId, userId }) => {
    setSelectedCompanyId(companyId);
    setSelectedEventId(eventId);
    setSelectedUserId(userId);
  };

  // Send test email handler
  const sendTestEmail = async () => {
    if (!testEmailAddress) return;

    setSendingTestEmail(true);
    setTestEmailResult(null);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch('/admin/email_templates/send_test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          html_content: htmlContent,
          recipient_email: testEmailAddress,
          email_type: emailType,
          company_id: selectedCompanyId,
          event_id: selectedEventId,
          user_id: selectedUserId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTestEmailResult({ success: true, message: data.message });
        setTimeout(() => {
          setShowTestEmailModal(false);
          setTestEmailResult(null);
        }, 2000);
      } else {
        setTestEmailResult({ success: false, message: data.error || 'Failed to send test email' });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestEmailResult({ success: false, message: 'Failed to send test email' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <div className={`template-preview-pane ${isExpanded ? 'expanded' : ''}`}>
      <div className="preview-header">
        <h6 className="preview-title">
          <i className="fa fa-eye mr-2"></i>
          Preview
          {isExpanded && (
            <span className="expanded-badge ml-2">Expanded</span>
          )}
        </h6>
        <div className="preview-actions">
          <button
            className="btn btn-sm btn-outline-primary action-btn"
            onClick={() => {
              setTestEmailResult(null);
              setShowTestEmailModal(true);
            }}
            disabled={!htmlContent}
            title="Send test email"
          >
            <i className="fa fa-paper-plane"></i>
          </button>
          {onToggleExpand && (
            <button
              className="btn btn-sm btn-outline-secondary action-btn"
              onClick={onToggleExpand}
              title={isExpanded ? "Collapse preview" : "Expand preview"}
            >
              <i className={`fa fa-${isExpanded ? 'compress' : 'expand'}`}></i>
            </button>
          )}
          {onOpenFullscreen && (
            <button
              className="btn btn-sm btn-outline-secondary action-btn"
              onClick={onOpenFullscreen}
              title="Open fullscreen preview"
            >
              <i className="fa fa-external-link"></i>
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-secondary action-btn"
            onClick={loadPreview}
            disabled={loading}
            title="Refresh preview"
          >
            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Record selectors */}
      <PreviewRecordSelector
        requiredContexts={requiredContexts}
        onSelectionChange={handleSelectionChange}
        selectedCompanyId={selectedCompanyId}
        selectedEventId={selectedEventId}
        selectedUserId={selectedUserId}
      />

      {/* Data usage indicator */}
      {dataUsed && (
        <div className="data-used-indicator">
          <small className="text-muted">
            Using: {' '}
            {dataUsed.company && <span className="badge badge-light mr-1">{dataUsed.company.name}</span>}
            {dataUsed.event && <span className="badge badge-light mr-1">{dataUsed.event.name}</span>}
            {dataUsed.user && (
              <span className="badge badge-light mr-1">
                {dataUsed.user.first_name} {dataUsed.user.last_name}
              </span>
            )}
          </small>
        </div>
      )}

      {/* Errors */}
      {previewErrors.length > 0 && (
        <div className="preview-errors">
          {previewErrors.map((error, idx) => (
            <div key={idx} className="alert alert-danger alert-sm mb-2">
              <i className="fa fa-exclamation-triangle mr-1"></i>
              {error}
            </div>
          ))}
        </div>
      )}

      {/* Preview iframe */}
      <div className="preview-content">
        {loading ? (
          <div className="preview-loading">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading preview...</p>
          </div>
        ) : previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            title="Email Preview"
            className="preview-iframe"
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="preview-empty">
            <i className="fa fa-file-alt fa-3x text-muted"></i>
            <p className="text-muted mt-2">Enter HTML content to see preview</p>
          </div>
        )}
      </div>

      {lastRefresh && (
        <div className="preview-footer">
          <small className="text-muted">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </small>
        </div>
      )}

      {/* Send Test Email Modal */}
      {showTestEmailModal && (
        <div className="test-email-modal-backdrop" onClick={() => setShowTestEmailModal(false)}>
          <div className="test-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="test-email-header">
              <h6>
                <i className="fa fa-paper-plane mr-2"></i>
                Send Test Email
              </h6>
              <button
                className="close-btn"
                onClick={() => setShowTestEmailModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="test-email-body">
              <p className="text-muted mb-3">
                Send a test email to verify your template looks correct before activating it.
              </p>
              <div className="form-group">
                <label htmlFor="test-email-input">Recipient Email</label>
                <input
                  type="email"
                  id="test-email-input"
                  className="form-control"
                  placeholder="you@example.com"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  disabled={sendingTestEmail}
                  autoFocus
                />
              </div>

              {testEmailResult && (
                <div className={`alert ${testEmailResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                  <i className={`fa ${testEmailResult.success ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                  {testEmailResult.message}
                </div>
              )}
            </div>
            <div className="test-email-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowTestEmailModal(false)}
                disabled={sendingTestEmail}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendTestEmail}
                disabled={!testEmailAddress || sendingTestEmail}
              >
                {sendingTestEmail ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fa fa-paper-plane mr-1"></i>
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .template-preview-pane {
          display: flex;
          flex-direction: column;
          height: 100%;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: white;
        }

        .preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          background: #f8f9fa;
        }

        .preview-title {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #495057;
        }

        .preview-actions {
          display: flex;
          gap: 4px;
        }

        .action-btn {
          padding: 4px 8px;
        }

        .expanded-badge {
          font-size: 10px;
          font-weight: normal;
          color: #28a745;
        }

        .data-used-indicator {
          padding: 8px 12px;
          background: #f0f9ff;
          border-bottom: 1px solid #dee2e6;
        }

        .preview-errors {
          padding: 8px;
          background: #fff5f5;
        }

        .alert-sm {
          padding: 8px 12px;
          font-size: 12px;
        }

        .preview-content {
          flex: 1;
          position: relative;
          overflow: auto;
          background: #ffffff;
        }

        .preview-loading,
        .preview-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #6c757d;
        }

        .preview-iframe {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
          /* Scale email content to fit preview pane when collapsed */
          transform: scale(0.6);
          transform-origin: top left;
          width: 166.67%; /* 100% / 0.6 to fill container after scale */
          height: 166.67%;
        }

        /* When expanded, show at full scale */
        .template-preview-pane.expanded .preview-iframe {
          transform: none;
          width: 100%;
          height: 100%;
        }

        .template-preview-pane.expanded .preview-content {
          display: flex;
          justify-content: center;
          background: #f0f0f0;
        }

        .template-preview-pane.expanded .preview-iframe {
          max-width: 650px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .preview-footer {
          padding: 8px 12px;
          border-top: 1px solid #dee2e6;
          background: #f8f9fa;
          text-align: right;
        }

        /* Test Email Modal */
        .test-email-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
        }

        .test-email-modal {
          background: white;
          border-radius: 8px;
          width: 400px;
          max-width: 90vw;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .test-email-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid #dee2e6;
        }

        .test-email-header h6 {
          margin: 0;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #6c757d;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .close-btn:hover {
          color: #343a40;
        }

        .test-email-body {
          padding: 16px;
        }

        .test-email-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px;
          border-top: 1px solid #dee2e6;
          background: #f8f9fa;
          border-radius: 0 0 8px 8px;
        }
      `}</style>
    </div>
  );
};

export default TemplatePreviewPane;
